// src/pages/PropertyListing.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useSearchParams, useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { getAllListings, syncAdminListings } from "../services/api";
import {
    Container,
    ControlBar,
    SearchInput,
    FilterGroup,
    FilterLabel,
    TableWrapper,
    Table,
    TableHead,
    TableRow,
    TableHeader,
    TableBody,
    TableData,
    StatusPill,
    ActionCell,
    PaginationContainer,
    PageButton,
    RowsSelect,
    PageSelect,
    SyncWrap,
    SyncButton,
    ErrorPopup,
    CloseButton
} from "../style-comps/PropertyListing.styles";
import syncIcon from '../Assets/arrow-round-left.png';
import checkIcon from "../Assets/check.svg";
import { ModelLoadingWrapper } from "../style-comps/DashboardHome.styles";

function readRoles() {
    const raw =
        localStorage.getItem("roles") ?? localStorage.getItem("userRoles") ?? "";
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
    } catch { }
    return raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
}

// const STAGE_OPTIONS = ["", "Draft", "Statement Sent", "Done"];
const DEFAULT_FILTERS = { listed: true, unlisted: true, parent: true, child: true, pmsOn: true, pmsOff: true };

// 每組（Status / Level / PMS Service）至少要留一個勾選，
// 兩個都取消的話該組判斷式恆為 false，會導致整份列表被濾成空的。
const FILTER_GROUPS = {
    listed: ["listed", "unlisted"],
    unlisted: ["listed", "unlisted"],
    parent: ["parent", "child"],
    child: ["parent", "child"],
    pmsOn: ["pmsOn", "pmsOff"],
    pmsOff: ["pmsOn", "pmsOff"],
};

const PropertyListing = () => {
    const [listings, setListings] = useState([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    // Initialize state from URL params
    const [search, setSearch] = useState(searchParams.get("q") || "");
    const [filters, setFilters] = useState({
        listed: searchParams.get("listed") !== "false",
        unlisted: searchParams.get("unlisted") !== "false",
        parent: searchParams.get("parent") !== "false",
        child: searchParams.get("child") !== "false",
        pmsOn: searchParams.get("pmsOn") !== "false",
        pmsOff: searchParams.get("pmsOff") !== "false",
    });
    // const [stageFilter, setStageFilter] = useState(searchParams.get("stage") || "");

    const [isLoading, setIsLoading] = useState(false);
    const [fadeOut, setFadeOut] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState("");
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const timerRef = useRef(null);
    const navigate = useNavigate();

    const [currentPage, setCurrentPage] = useState(
        Math.max(1, parseInt(searchParams.get("page") || "1", 10))
    );

    const roles = readRoles();
    const isAdmin = roles.includes("Admin") || roles.includes("SuperAdmin");

    // ---- Loading ----
    const startLoading = () => {
        if (timerRef.current) clearInterval(timerRef.current);
        setIsLoading(true);
        setFadeOut(false);
        setProgress(0);
        let percent = 0;
        timerRef.current = setInterval(() => {
            percent += Math.floor(Math.random() * 10) + 5;
            setProgress(Math.min(percent, 99));
        }, 250);
    };

    const stopLoading = () => {
        if (timerRef.current) clearInterval(timerRef.current);
        setProgress(100);
        setFadeOut(true);
        setTimeout(() => setIsLoading(false), 500);
    };

    useEffect(() => {
        return () => timerRef.current && clearInterval(timerRef.current);
    }, []);

    // ---- Fetch Data ----

    const fetchListings = async () => {
        try {
            startLoading();
            const data = await getAllListings();
            const arr = Array.isArray(data)
                ? data
                : Array.isArray(data?.data)
                    ? data.data
                    : [];

            // 模擬 Statement Stage
            // const mockStage = ["Draft", "Statement Sent", "Done"];
            // const arrWithStage = arr.map((item, i) => ({
            //     ...item,
            //     statementStage: mockStage[i % 3],
            // }));

            setListings(arr);
            setError("");
        } catch (e) {
            const msg =
                e?.response?.data?.message || e.message || "Failed to load listings";
            console.error("❌ 取得 Listings 失敗：", msg);
            setError(msg);
            setListings([]);
        } finally {
            stopLoading();
        }
    };
    useEffect(() => {
        fetchListings();
    }, []);


    const handleSyncListings = async () => {
        startLoading();
        try {
            const result = await syncAdminListings();
            const msg = `Sync Listings successfully: ${result?.message || 'Operation completed successfully.'}`;
            setErrorMessage(msg);
            setShowError(true);
            await fetchListings();
        } catch (err) {
            const errMsg = `Sync Listings failed: ${err?.response?.data?.message || err.message}`;
            setErrorMessage(errMsg);
            setShowError(true);
        } finally {
            stopLoading();
        }
    };

    // ---- Sync filters/search/page to URL ----
    useEffect(() => {
        const sp = new URLSearchParams();
        if (search) sp.set("q", search);
        if (!filters.listed) sp.set("listed", "false");
        if (!filters.unlisted) sp.set("unlisted", "false");
        if (!filters.parent) sp.set("parent", "false");
        if (!filters.child) sp.set("child", "false");
        if (!filters.pmsOn) sp.set("pmsOn", "false");
        if (!filters.pmsOff) sp.set("pmsOff", "false");
        // if (stageFilter) sp.set("stage", stageFilter);
        sp.set("page", String(currentPage));
        setSearchParams(sp, { replace: true });
    }, [search, filters, currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

    // ---- Filter + Search ----
    const filtered = useMemo(() => {
        // 搜尋正規化：移除零寬字元（\u200B–\u200D、\uFEFF，複製貼上常夾帶、\s 抓不到），
        // 並把連續空白壓成單一半形空白（\s 亦涵蓋不斷行空白 \u00A0 與全形空白 \u3000）。
        // 避免資料裡的隱形/重複空白造成「完整名稱搜不到、部分名稱卻搜得到」。
        const norm = (s) =>
            String(s ?? "")
                .toLowerCase()
                .replace(/[\u200B-\u200D\uFEFF]/g, "")
                .replace(/\s+/g, " ")
                .trim();
        const q = norm(search);
        const queryTokens = q.split(" ").filter(Boolean);

        return listings.filter((x) => {
            const listedVal = x.isListed ?? x.IsListed ?? 0;
            const isListed = Number(listedVal) === 1 || listedVal === true;

            const parentId = x.ParentListingId ?? x.parentListingId ?? null;
            const isParent = parentId === null;

            // OR within group; if both unchecked → nothing passes (false)
            const rawPms = x.IsServicePms ?? x.isServicePms;
            const isServicePms = rawPms === true || rawPms === 1;
            const statusPass = (filters.listed && isListed) || (filters.unlisted && !isListed);
            const levelPass = (filters.parent && isParent) || (filters.child && !isParent);
            const pmsPass = (filters.pmsOn && isServicePms) || (filters.pmsOff && !isServicePms);  // 👈 加這行
            if (!(statusPass && levelPass && pmsPass)) return false;

            if (!q) return true;
            const id = String(x.id ?? x.Id ?? "");
            const name = norm(x.name || x.Name);
            const kg = norm(x.nickName || x.NickName);
            const city = norm(x.city || x.City);
            // 把可搜尋的欄位併成一段（同樣正規化過），只要每個字詞都出現即算命中。
            // 這樣完整名稱、部分名稱、甚至字詞順序不同都找得到。
            const haystack = `${id.toLowerCase()} ${name} ${kg} ${city}`;
            return queryTokens.every((t) => haystack.includes(t));
        });
    }, [listings, search, filters]);

    // ---- Pagination ----
    const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
    const safePage = Math.min(currentPage, totalPages);
    const pageStart = (safePage - 1) * itemsPerPage;
    const pageData = filtered.slice(pageStart, pageStart + itemsPerPage);

    const handlePageChange = (page) => {
        setCurrentPage(Math.min(Math.max(1, page), totalPages));
    };

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(parseInt(e.target.value));
        setCurrentPage(1);
    };

    const handlePageSelectChange = (e) => {
        handlePageChange(parseInt(e.target.value));
    };

    const toggleFilter = (key) => {
        setFilters((prev) => {
            const isTurningOff = prev[key];
            if (isTurningOff) {
                const group = FILTER_GROUPS[key];
                const anyOtherStillOn = group.some((k) => k !== key && prev[k]);
                if (!anyOtherStillOn) {
                    setErrorMessage("At least one option must stay selected in each filter group.");
                    setShowError(true);
                    return prev;
                }
            }
            return { ...prev, [key]: !prev[key] };
        });
        setCurrentPage(1);
    };

    const handleSearch = (e) => {
        setSearch(e.target.value);
        setCurrentPage(1);
    };

    // const handleStageFilter = (e) => {
    //     setStageFilter(e.target.value);
    //     setCurrentPage(1);
    // };

    const handleReset = () => {
        setSearch("");
        setFilters(DEFAULT_FILTERS);
        // setStageFilter("");
        setCurrentPage(1);
    };



    const isFiltered =
        search ||
        // stageFilter ||
        !filters.listed ||
        !filters.unlisted ||
        !filters.parent ||
        !filters.child;

    if (!isAdmin) return <Navigate to="/dashboard" replace />;

    if (isLoading) {
        return (
            <ModelLoadingWrapper fadeOut={fadeOut}>
                <p style={{ fontSize: 18, color: "#61CBC9", fontWeight: 700 }}>
                    Loading Listings... {Math.min(progress, 100)}%
                </p>
            </ModelLoadingWrapper>
        );
    }

    return (
        <Container>
            <ControlBar>
                <SearchInput
                    value={search}
                    onChange={handleSearch}
                    placeholder="Search by ID / Name / NickName / City"
                />

                {/* 篩選區 */}
                <FilterGroup>
                    <span style={{ fontSize: 12, color: "#999", fontWeight: 600, marginRight: 4 }}>
                        Status:
                    </span>
                    {["listed", "unlisted"].map((key) => (
                        <FilterLabel key={key} onClick={() => toggleFilter(key)} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                            <div style={{
                                width: 16, height: 16,
                                border: filters[key] ? "none" : "1.5px solid #aaa",
                                borderRadius: 3,
                                background: filters[key] ? "#61CBC9" : "#fff",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                flexShrink: 0,
                                transition: "background 0.15s",
                            }}>
                                {filters[key] && <img src={checkIcon} alt="check" style={{ width: 10, height: 10, filter: "brightness(0) invert(1)" }} />}
                            </div>
                            {key.charAt(0).toUpperCase() + key.slice(1)}
                        </FilterLabel>
                    ))}

                    <span style={{ fontSize: 12, color: "#999", fontWeight: 600, marginLeft: 12, marginRight: 4 }}>
                        Level:
                    </span>
                    {["parent", "child"].map((key) => (
                        <FilterLabel key={key} onClick={() => toggleFilter(key)} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                            <div style={{
                                width: 16, height: 16,
                                border: filters[key] ? "none" : "1.5px solid #aaa",
                                borderRadius: 3,
                                background: filters[key] ? "#61CBC9" : "#fff",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                flexShrink: 0,
                                transition: "background 0.15s",
                            }}>
                                {filters[key] && <img src={checkIcon} alt="check" style={{ width: 10, height: 10, filter: "brightness(0) invert(1)" }} />}
                            </div>
                            {key.charAt(0).toUpperCase() + key.slice(1)}
                        </FilterLabel>
                    ))}

                    <span style={{ fontSize: 12, color: "#999", fontWeight: 600, marginLeft: 12, marginRight: 4 }}>
                        PMS Service:
                    </span>
                    {[
                        { key: "pmsOn", label: "On" },
                        { key: "pmsOff", label: "Off" },
                    ].map(({ key, label }) => (
                        <FilterLabel key={key} onClick={() => toggleFilter(key)} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                            <div style={{
                                width: 16, height: 16,
                                border: filters[key] ? "none" : "1.5px solid #aaa",
                                borderRadius: 3,
                                background: filters[key] ? "#61CBC9" : "#fff",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                flexShrink: 0,
                                transition: "background 0.15s",
                            }}>
                                {filters[key] && <img src={checkIcon} alt="check" style={{ width: 10, height: 10, filter: "brightness(0) invert(1)" }} />}
                            </div>
                            {label}
                        </FilterLabel>
                    ))}

                    {/* <span style={{ fontSize: 12, color: "#999", fontWeight: 600, marginLeft: 12, marginRight: 4 }}>
                        Stage:
                    </span>
                    <select
                        value={stageFilter}
                        onChange={handleStageFilter}
                        style={{ fontSize: 13, padding: "2px 6px", borderRadius: 4, border: "1px solid #ddd" }}
                    >
                        {STAGE_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>{opt || "All"}</option>
                        ))}
                    </select> */}


                    <button onClick={handleReset}
                        style={{
                            marginLeft: 12,
                            fontSize: 12,
                            padding: "3px 10px",
                            borderRadius: 4,
                            border: "1px solid #ccc",
                            background: "#f5f5f5",
                            cursor: "pointer",
                            color: "#555",
                        }}
                    >
                        Reset
                    </button>

                </FilterGroup>
            </ControlBar>

            {error && (
                <div style={{ color: "crimson", margin: "8px 0 12px", fontWeight: 600 }}>
                    {error}
                </div>
            )}

            {!error && pageData.length === 0 && (
                <div style={{ textAlign: "center", color: "#777", marginTop: 24 }}>
                    No listings found.
                </div>
            )}

            <TableWrapper>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableHeader style={{ width: 150 }}>Listing Id</TableHeader>
                            <TableHeader style={{ width: 250 }}>NickName</TableHeader>
                            <TableHeader style={{ width: 350 }}>Name</TableHeader>
                            <TableHeader>Parent</TableHeader>
                            <TableHeader>Listed</TableHeader>
                            <TableHeader>PMS Service</TableHeader>
                            {/* <TableHeader>Statement Stage</TableHeader> */}
                            <TableHeader>Action</TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {pageData.map((item) => {
                            const id = item.id ?? item.Id;
                            const kg = item.kozyGuruName ?? item.NickName ?? "-";
                            const name = item.name ?? item.Name ?? "-";
                            const listedVal = item.isListed ?? item.IsListed ?? 0;
                            const isListed = Number(listedVal) === 1 || listedVal === true;
                            // const stage = item.statementStage || "Draft";
                            const parentId = item.ParentListingId ?? item.parentListingId ?? null;
                            const isParentText = parentId === null ? "Parent" : "Child";
                            const rawPms = item.IsServicePms ?? item.isServicePms;
                            const isServicePms = rawPms === true || rawPms === 1;
                            // PMS Off + Listed：房源還在賣但沒被同步，需要特別注意
                            // PMS Off + Unlisted：正常關閉，低優先，整列淡化即可
                            const isOffButListed = !isServicePms && isListed;
                            const isOffAndUnlisted = !isServicePms && !isListed;
                            const rowState = isOffButListed ? "off-listed" : isOffAndUnlisted ? "off-unlisted" : undefined;

                            return (
                                <TableRow key={id} $rowState={rowState}>
                                    <TableData style={{ width: 150, maxWidth: 150 }}>
                                        {id}
                                        {isOffButListed && (
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 24 24"
                                                width="13"
                                                height="13"
                                                fill="none"
                                                stroke="#c06a12"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                data-tooltip-id="pms-off-listed-tooltip"
                                                data-tooltip-content="PMS is off, but this listing is still Listed"
                                                style={{ marginLeft: 6, verticalAlign: "middle", cursor: "help" }}
                                            >
                                                <path d="M10.24 3.957 2.34 17.5a1.914 1.914 0 0 0 1.66 2.87h15.8a1.914 1.914 0 0 0 1.66-2.87l-7.9-13.543a1.914 1.914 0 0 0-3.32 0Z" />
                                                <line x1="12" y1="9" x2="12" y2="13" />
                                                <line x1="12" y1="16.5" x2="12" y2="16.5" />
                                            </svg>
                                        )}
                                    </TableData>
                                    <TableData style={{ width: 250, maxWidth: 250 }}>{kg}</TableData>
                                    <TableData style={{ width: 350, maxWidth: 350 }}>{name}</TableData>
                                    <TableData>
                                        <StatusPill active={isParentText === "Parent"} $muted={isOffAndUnlisted}>
                                            {isParentText}
                                        </StatusPill>
                                    </TableData>
                                    <TableData>
                                        <StatusPill
                                            active={isListed}
                                            $variant={isOffButListed ? "warning" : undefined}
                                            $muted={isOffAndUnlisted}
                                        >
                                            {isListed ? "Listed" : "Unlisted"}
                                        </StatusPill>
                                    </TableData>
                                    <TableData>                         {/* 👈 加這段 */}
                                        <StatusPill active={isServicePms}>
                                            {isServicePms ? "On" : "Off"}
                                        </StatusPill>
                                    </TableData>
                                    {/* <TableData>
                                        <StatementPill stage={stage}>{stage}</StatementPill>
                                    </TableData> */}
                                    <ActionCell>
                                        {!isServicePms ? (
                                            // PMS Off：直接鎖頭，不論 Parent / Child
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 24 24"
                                                width="18"
                                                height="18"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                title="PMS Service is off, cannot be edited"
                                                style={{ opacity: 0.8 }}
                                            >
                                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                            </svg>
                                        ) : isParentText === "Parent" ? (
                                            <>
                                                {/* Edit */}
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                    width="18"
                                                    height="18"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    style={{ cursor: "pointer" }}
                                                    title="Edit listing"
                                                    onClick={() => navigate(`/dashboard/property/${id}`)}
                                                >
                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                                </svg>
                                            </>
                                        ) : (
                                            // Child：顯示鎖頭，不可編輯
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 24 24"
                                                width="18"
                                                height="18"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                title="Child listing cannot be edited"
                                                style={{ opacity: 0.8 }}
                                            >
                                                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                            </svg>
                                        )}
                                    </ActionCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableWrapper>

            {/* 分頁區 */}
            <PaginationContainer>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 14, color: "#777" }}>Rows per page:</span>
                    <RowsSelect value={itemsPerPage} onChange={handleItemsPerPageChange}>
                        {[10, 20, 50].map((n) => (
                            <option key={n} value={n}>
                                {n}
                            </option>
                        ))}
                    </RowsSelect>
                </div>

                {/* 中間：PageButton & PageSelect 置中 */}
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <PageButton disabled={safePage === 1} onClick={() => handlePageChange(safePage - 1)}>
                        ←
                    </PageButton>

                    <PageSelect value={safePage} onChange={handlePageSelectChange}>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                            <option key={p} value={p}>
                                {p}
                            </option>
                        ))}
                    </PageSelect>

                    <PageButton disabled={safePage === totalPages} onClick={() => handlePageChange(safePage + 1)}>
                        →
                    </PageButton>
                </div>

                {/* 右邊：筆數靠右 */}
                <span style={{ fontSize: 13, color: "#777" }}>
                    {isFiltered ? `${filtered.length} results` : `${listings.length} total`}
                </span>
            </PaginationContainer>
            <SyncWrap>
                <SyncButton onClick={handleSyncListings}>
                    <img src={syncIcon} alt="sync" />
                    Sync Listings
                </SyncButton>
            </SyncWrap>

            <ErrorPopup show={showError}>
                {errorMessage}
                <CloseButton onClick={() => setShowError(false)}>×</CloseButton>
            </ErrorPopup>

            <Tooltip
                id="pms-off-listed-tooltip"
                place="right"
                delayShow={150}
                style={{ backgroundColor: "#3ed6d4", color: "#fff", zIndex: 1200 }}
            />
        </Container>


    );
};

export default PropertyListing;
